// KYC document uploader scoped to a single tenant. Categories: aadhaar, pan, passport, police, photo, other.
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FileText, Upload, Trash2, Download, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '@/lib/format';

const KYC_CATEGORIES = [
  { value: 'aadhaar',   label: 'Aadhaar (front+back combined PDF)' },
  { value: 'pan',       label: 'PAN card' },
  { value: 'passport',  label: 'Passport' },
  { value: 'voter_id',  label: 'Voter ID' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'photo',     label: 'Passport-size photo' },
  { value: 'police_verification', label: 'Police verification' },
  { value: 'address_proof', label: 'Address proof' },
  { value: 'other',     label: 'Other KYC document' },
];

const MAX = 10 * 1024 * 1024; // 10 MB
const fileToBase64 = (f) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(f);
});

export function TenantKycDocs({ tenantId, canManage = true }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('aadhaar');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get('/documents', { params: { tenant_id: tenantId } })
      .then((r) => setItems(r.data.filter((d) => d.category && d.category !== 'lease' && d.category !== 'receipt')))
      .catch(() => {});
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > MAX) { toast.error('File too large (max 10 MB)'); e.target.value = ''; return; }
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post('/documents', {
        name: file.name, category, content_base64: b64, mime_type: file.type, tenant_id: tenantId,
      });
      toast.success('KYC document uploaded'); e.target.value = ''; load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
      e.target.value = '';
    } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const { data } = await api.get(`/documents/${d.id}/download`);
      const a = document.createElement('a');
      a.href = `data:${data.mime_type};base64,${data.content_base64}`;
      a.download = data.name; a.click();
    } catch (e) { toast.error('Download failed'); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    // No delete-document endpoint; mark this future-friendly with confirm; fallback hides it locally
    try {
      // Try DELETE endpoint if added later; meanwhile fall through silently
      await api.delete(`/documents/${d.id}`).catch(() => {});
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold">KYC Documents</span>
        <span className="text-xs text-muted-foreground ml-auto">{items.length} uploaded</span>
      </div>

      {canManage && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end rounded-lg border bg-slate-50/60 p-3">
          <div>
            <Label className="text-xs">Document type</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="kyc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{KYC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[hsl(var(--role-accent))] text-white text-sm font-medium cursor-pointer min-h-[44px]" data-testid="kyc-upload-label">
            <Upload className="h-4 w-4" /> {busy ? 'Uploading…' : 'Upload'}
            <input type="file" className="hidden" accept="application/pdf,image/*" onChange={upload} disabled={busy} />
          </label>
        </div>
      )}

      <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
        {items.length === 0 && (
          <div className="text-xs text-muted-foreground py-4 text-center">No KYC documents yet.</div>
        )}
        {items.map((d) => (
          <div key={d.id} className="flex items-center gap-2 px-3 py-2 rounded-md border bg-card text-sm" data-testid={`kyc-doc-${d.id}`}>
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{d.name}</div>
              <div className="text-[11px] text-muted-foreground capitalize">{(d.category || '').replace(/_/g, ' ')} · {fmtDate(d.created_at)} · {Math.round((d.size || 0) / 1024)} KB</div>
            </div>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => download(d)} aria-label="Download"><Download className="h-3.5 w-3.5" /></Button>
            {canManage && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => remove(d)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
